import React from 'react'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Button } from '../../../../components/Button/Button'
import PrizeBadge from '../../../../components/ScopedBlock/PrizeBadge.svg?url'
import { useNavigate } from 'react-router-dom'
import { Form } from 'react-bootstrap'
import { SelectDropdown } from '../../../../components/SelectDropdown/SelectDropdown'
import { EmailAction } from '../../../../models/Account'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetUncategorizedEmailActionQuery, useSetUncategorizedEmailActionMutation } from '../../../../store/integrations/emailTypes'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { EMAIL_ACTION_OPTIONS } from '../../EmailTypes/utils'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import './EmailTypeSettings.scss'

export const EmailTypeSettings = () => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const { hasQuota } = useZocksQuota()
  const emailEnabled = hasQuota('integration.emailEnabled.value')

  const { data: uncategorizedEmailAction, isLoading: uncategorizedEmailActionLoading } = useGetUncategorizedEmailActionQuery({ accountId })
  const [updateUncategorizedEmailAction, { isLoading: uncategorizedEmailActionUpdating }] = useSetUncategorizedEmailActionMutation()

  if (uncategorizedEmailActionLoading) {
    return <div className='skeleton' style={{ width: '100%', height: '60px' }} />
  }

  const _handleUncategorizedEmailActionSelected = (action: EmailAction) => {
    updateUncategorizedEmailAction({
      accountId,
      payload: {
        emailAction: action,
      },
    })
  }

  const handleUncategorizedEmailActionSelected = tracked(
    _handleUncategorizedEmailActionSelected,
    { eventName: TrackEvent.EMAIL_AUTOMATION_UNCATEGORIZED_ACTION_CHANGED },
    {
      eventDetailsExtractor: (action: EmailAction) => {
        return {
          action,
        }
      },
    },
  )

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Uncategorized email handling</h4>
      <div className='d-flex zocks-p-1.25 gap-3 align-items-center zocks-bg-color-primary-light zocks-border-0.5'>
        <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
        <span className='paragraph-medium zocks-text-gray-900'>
          Configure actions for specific email types in the Email Types tab. This setting applies when your AI Assistant can't match an email to any category.
        </span>
        {!emailEnabled && (
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Button
              className='d-flex p-1 p-2 align-items-center text-nowrap'
              variant='primary'
              onClick={() => {
                navigate('/account/plan')
              }}
            >
              <img alt='Upgrade' src={PrizeBadge} />
              Get feature
            </Button>
          </ScopedBlock>
        )}
      </div>
      <div className='d-flex flex-column zocks-gap-2'>
        <div className='paragraph-medium'>Choose what happens when an email doesn't fit any of your Email types.</div>
        <div className='flex-column gap-1'>
          <Form.Label className='paragraph-medium'>Select action</Form.Label>
          <div className='w-50'>
            <ScopedBlock
              scope={{
                scope: 'email_type:update',
                superScope: ['email_type:admin'],
              }}
              missingScopeAction={MissingScopeAction.REPLACE}
              replaceElement={
                <Tooltip className={'uncategorized-action-disabled-tooltip'} title={'Your account admin can edit this setting.'}>
                  <SelectDropdown
                    options={EMAIL_ACTION_OPTIONS}
                    initialSelectedOption={EMAIL_ACTION_OPTIONS.find((option) => {
                      return option.id === uncategorizedEmailAction?.emailAction
                    })}
                    disabled={true}
                  />
                </Tooltip>
              }
            >
              <SelectDropdown
                options={EMAIL_ACTION_OPTIONS}
                initialSelectedOption={EMAIL_ACTION_OPTIONS.find((option) => {
                  return option.id === uncategorizedEmailAction?.emailAction
                })}
                onOptionSelected={(action) => handleUncategorizedEmailActionSelected(action.id as EmailAction)}
                disabled={uncategorizedEmailActionUpdating || !emailEnabled}
              />
            </ScopedBlock>
          </div>
        </div>
      </div>
    </div>
  )
}
